// Blood-pressure checks for the intake questionnaires.
//
// 1. Mix-up check — diastolic page, blocks Continue (this section).
// 2. Extreme-reading warnings — systolic page, advisory only (second
//    section, at the end of the file).
//
// The systolic ("top number") and diastolic ("bottom number") questions are
// asked on consecutive pages, each as a RANGE ("120-139", "100 or over",
// "Under 95"). A patient who swaps the two numbers, or misreads the cuff,
// produces a pair whose bottom number cannot sit below the top one — and the
// saved intake then reads as a hypotensive or hypertensive patient, which is
// exactly what a beta-blocker prescriber decides on. This catches the pair on
// the diastolic page, before the answers are saved, while the patient can
// still fix it.
//
// Rule: a pair is flagged when the diastolic range STARTS at or above where
// the systolic range starts (diastolic.min >= systolic.min). With the current
// labels that flags exactly the four pairs no real reading produces — even the
// most favourable numbers inside the two ranges leave a pulse pressure under
// 20 mmHg — and nothing else:
//
//   systolic \ diastolic   100 or over  90-99  80-90  65-79  Under 65
//   160 or higher          ok           ok     ok     ok     ok
//   140-159                ok           ok     ok     ok     ok
//   120-139                ok           ok     ok     ok     ok
//   95-119                 FLAG         ok     ok     ok     ok
//   Under 95               FLAG         FLAG   FLAG   ok     ok
//
// A plain "could the two ranges overlap" test is deliberately NOT used: the
// open-ended top buckets overlap every diastolic bucket, and it would also
// flag ordinary readings such as 130/100 and 90/70. The closed end of an
// open-ended label ("Under N", "N or higher") comes from the server-side
// intake validator's physiologic bounds (app/service/intake_validator.go:
// systolic 70–200, diastolic 40–130) so both sides reason about the same
// range.
//
// The check fails OPEN: an unparseable label, a missing systolic answer, or a
// question that isn't the diastolic one yields no flag. A questionnaire edit
// that breaks label parsing is caught by bloodPressureCheck.test.js, which
// parses every blood-pressure label in every questionnaire.

export const QUESTION_TYPE_BLOOD_PRESSURE_SYSTOLIC = "bloodpressure-systolic";
export const QUESTION_TYPE_BLOOD_PRESSURE_DIASTOLIC = "bloodpressure-diastolic";

// Mirrors validateBloodPressure in app/service/intake_validator.go.
export const SYSTOLIC_BOUNDS = { min: 70, max: 200 };
export const DIASTOLIC_BOUNDS = { min: 40, max: 130 };

const RANGE_LABEL = /^(\d+)\s*(?:-|–|to)\s*(\d+)$/i;
const AT_LEAST_LABEL = /^(\d+)\s+(?:or|and)\s+(?:higher|over|more|above|up)$/i;
const UNDER_LABEL = /^(?:under|below|less than)\s+(\d+)$/i;

/**
 * The inclusive { min, max } a choice label stands for, or null when the label
 * isn't a range we understand. `bounds` closes the open end of "N or higher"
 * and "Under N".
 */
export function parseBloodPressureRange(label, bounds) {
  const text = String(label || "").trim();

  let match = text.match(RANGE_LABEL);
  if (match) {
    const min = Number(match[1]);
    const max = Number(match[2]);
    return min <= max ? { min, max } : null;
  }

  match = text.match(AT_LEAST_LABEL);
  if (match) {
    const min = Number(match[1]);
    return { min, max: Math.max(min, bounds.max) };
  }

  match = text.match(UNDER_LABEL);
  if (match) {
    const max = Number(match[1]) - 1;
    return { min: Math.min(bounds.min, max), max };
  }

  return null;
}

/**
 * Whether a systolic/diastolic pair of choice labels can't be a real reading:
 * the diastolic range starts at or above where the systolic range starts.
 * A label that doesn't parse never flags.
 */
export function isBloodPressureMixUp(systolicLabel, diastolicLabel) {
  const systolic = parseBloodPressureRange(systolicLabel, SYSTOLIC_BOUNDS);
  const diastolic = parseBloodPressureRange(diastolicLabel, DIASTOLIC_BOUNDS);
  if (!systolic || !diastolic) return false;
  return diastolic.min >= systolic.min;
}

/**
 * The mix-up on the question at `index`, or null. Only the diastolic question
 * is ever flagged, against the nearest systolic question before it. The result
 * carries both chosen labels — the systolic answer sits on the previous page,
 * out of the patient's sight — and the systolic question's index so the UI can
 * send them back to it.
 */
export function findBloodPressureMixUp(questionnaire, answers, index) {
  const questions = questionnaire?.questions || [];
  if (questions[index]?.type !== QUESTION_TYPE_BLOOD_PRESSURE_DIASTOLIC) {
    return null;
  }

  let systolicIndex = -1;
  for (let i = index - 1; i >= 0; i--) {
    if (questions[i]?.type === QUESTION_TYPE_BLOOD_PRESSURE_SYSTOLIC) {
      systolicIndex = i;
      break;
    }
  }
  if (systolicIndex < 0) return null;

  // Radio answers record the chosen choice's label on `selected` (see
  // consultationQuestion's handleRadioChange).
  const systolic = answers?.[systolicIndex]?.selected || "";
  const diastolic = answers?.[index]?.selected || "";
  if (!systolic || !diastolic) return null;
  if (!isBloodPressureMixUp(systolic, diastolic)) return null;

  return { systolicIndex, systolic, diastolic };
}

/**
 * The patient-facing copy for a mix-up returned by findBloodPressureMixUp, as
 * two paragraphs of runs for IntakeWarningNote: what's wrong, then what to do.
 * The first names both answers in bold (`{ strong }` runs, no quotation marks)
 * because the top number is on the previous page; the second's `{ action }`
 * run is the note's one button, which takes the patient back to it. Empty
 * when there's no mix-up.
 */
export function bloodPressureMixUpMessage(mixUp) {
  if (!mixUp) return [];
  return [
    [
      "Looks like a mix-up: you chose ",
      { strong: mixUp.systolic },
      " for your top number and ",
      { strong: mixUp.diastolic },
      " for your bottom number, but the bottom can’t be higher than the top.",
    ],
    [
      "Choose a different bottom number, or ",
      { action: "change your top number" },
      ".",
    ],
  ];
}

/**
 * The index of the first diastolic question whose answer conflicts with its
 * systolic answer, or -1. The final submit uses this so a pair that became
 * inconsistent after the fact (the patient went back, changed the top number,
 * then jumped ahead by URL) is sent back to the diastolic page, not saved.
 */
export function firstBloodPressureMixUpIndex(questionnaire, answers) {
  const questions = questionnaire?.questions || [];
  for (let i = 0; i < questions.length; i++) {
    if (findBloodPressureMixUp(questionnaire, answers, i)) return i;
  }
  return -1;
}

// ---------------------------------------------------------------------------
// Extreme-reading warnings (systolic page).
//
// The top and bottom buckets — "160 or higher" and "Under 95" — each get a
// soft note under the question, above Continue, asking the patient to
// confirm it's their most recent reading. Neither blocks: an extreme reading
// may well be real, and the doctor sees it either way (triage already routes
// both buckets to review — app/service/visittriage/validators/blood_pressure.go
// accepts 95–159).
//
// The thresholds apply to the parsed range, not the label text, so a
// relabelled or split bucket keeps its warning: a range that STARTS at or
// above SYSTOLIC_HIGH_WARNING_MIN is high, one that ENDS below
// SYSTOLIC_LOW_WARNING_MAX is low. An unparseable label never warns.

export const SYSTOLIC_HIGH_WARNING_MIN = 160;
export const SYSTOLIC_LOW_WARNING_MAX = 95;

export const SYSTOLIC_WARNING_HIGH = "high";
export const SYSTOLIC_WARNING_LOW = "low";

/**
 * SYSTOLIC_WARNING_HIGH or SYSTOLIC_WARNING_LOW when a systolic choice label
 * sits in one of the two extreme buckets, else null.
 */
export function systolicWarningKind(label) {
  const range = parseBloodPressureRange(label, SYSTOLIC_BOUNDS);
  if (!range) return null;
  if (range.min >= SYSTOLIC_HIGH_WARNING_MIN) return SYSTOLIC_WARNING_HIGH;
  if (range.max < SYSTOLIC_LOW_WARNING_MAX) return SYSTOLIC_WARNING_LOW;
  return null;
}

/**
 * The extreme-reading warning for the question at `index`, or null. Only the
 * systolic question is ever flagged, from its own answer. The result carries
 * the kind and the chosen label.
 */
export function findSystolicWarning(questionnaire, answers, index) {
  const questions = questionnaire?.questions || [];
  if (questions[index]?.type !== QUESTION_TYPE_BLOOD_PRESSURE_SYSTOLIC) {
    return null;
  }
  const systolic = answers?.[index]?.selected || "";
  if (!systolic) return null;
  const kind = systolicWarningKind(systolic);
  if (!kind) return null;
  return { kind, systolic };
}

/**
 * The patient-facing copy for a warning returned by findSystolicWarning, as
 * one paragraph for IntakeWarningNote. Empty when there's no warning.
 */
export function systolicWarningMessage(warning) {
  if (!warning) return [];
  if (warning.kind === SYSTOLIC_WARNING_HIGH) {
    return [
      "This is an abnormally high blood pressure. Are you sure this is your most recent reading?",
    ];
  }
  if (warning.kind === SYSTOLIC_WARNING_LOW) {
    return [
      "This is a very low blood pressure. Are you sure this is your most recent reading?",
    ];
  }
  return [];
}
